import React from 'react';
import { Plus, User, Calendar } from 'lucide-react';
import { LabSample, TestResult } from '../../contexts/LabSampleContext';

interface LabSampleTestResultsProps {
    sample: LabSample;
    showAddTest: boolean;
    setShowAddTest: (value: boolean) => void;
    newTestResult: Partial<TestResult>;
    setNewTestResult: (result: Partial<TestResult>) => void;
    onAddResult: () => void;
}

export const LabSampleTestResults: React.FC<LabSampleTestResultsProps> = ({
    sample,
    showAddTest,
    setShowAddTest,
    newTestResult,
    setNewTestResult,
    onAddResult,
}) => {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Test Results</h2>
                <button
                    onClick={() => setShowAddTest(!showAddTest)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                    <Plus className="w-4 h-4" />
                    Add Result
                </button>
            </div>

            {showAddTest && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-bold text-gray-900 mb-3">Add Test Result</h3>
                    <div className="space-y-3">
                        <input
                            type="text"
                            placeholder="Test Type (e.g., Hardness Testing)"
                            value={newTestResult.testType || ''}
                            onChange={e => setNewTestResult({ ...newTestResult, testType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <input
                            type="text"
                            placeholder="Performed By"
                            value={newTestResult.performedBy || ''}
                            onChange={e => setNewTestResult({ ...newTestResult, performedBy: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <input
                            type="date"
                            value={newTestResult.performedDate || ''}
                            onChange={e => setNewTestResult({ ...newTestResult, performedDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <select
                            value={newTestResult.result || 'pass'}
                            onChange={e => setNewTestResult({ ...newTestResult, result: e.target.value as 'pass' | 'fail' | 'inconclusive' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                            <option value="pass">Pass</option>
                            <option value="fail">Fail</option>
                            <option value="inconclusive">Inconclusive</option>
                        </select>
                        <textarea
                            placeholder="Test Findings..."
                            value={newTestResult.findings || ''}
                            onChange={e => setNewTestResult({ ...newTestResult, findings: e.target.value })}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <div className="flex gap-2">
                            <button
                                onClick={onAddResult}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Save Result
                            </button>
                            <button
                                onClick={() => setShowAddTest(false)}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {sample.testResults.length > 0 ? (
                <div className="space-y-4">
                    {sample.testResults.map(result => (
                        <div key={result.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-bold text-gray-900">{result.testType}</h3>
                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${result.result === 'pass'
                                        ? 'bg-green-100 text-green-800'
                                        : result.result === 'fail'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                        }`}
                                >
                                    {result.result.toUpperCase()}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{result.findings}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {result.performedBy}
                                </span>
                                <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(result.performedDate).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-gray-500 text-center py-8">No test results recorded yet.</p>
            )}
        </div>
    );
};
